import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

import { env } from '@/shared/lib/env';

import { normalizeError } from './errors';
import { sessionToken } from './session-token';

/** Endpoints that must never carry (or react to) a session token. */
const PUBLIC_PATHS = [
  '/auth/request-otp',
  '/auth/verify-otp',
  '/auth/miniapp',
  '/auth/demo',
  '/payments/callback',
];

function isPublicPath(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_PATHS.some((path) => url.startsWith(path));
}

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

/* ----------------------------- Request phase ----------------------------- */

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = sessionToken.get();
  if (token && !isPublicPath(config.url)) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/* ---------------------------- Response phase ----------------------------- */

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Register what happens when the server rejects our token. The auth feature
 * installs a handler that clears the session and routes to the login screen;
 * keeping it as a callback avoids a `shared → features` import.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/**
 * A failed `responseType: 'blob'` request still carries a JSON error body —
 * as a Blob. Left alone, the error model would see an unreadable object and
 * fall back to a generic message, so it is decoded back to JSON first.
 */
async function decodeBlobError(error: unknown): Promise<void> {
  if (!axios.isAxiosError(error)) return;
  const data: unknown = error.response?.data;
  if (!(data instanceof Blob)) return;
  if (!data.type.includes('json') && data.size > 4096) return;

  try {
    const text = await data.text();
    if (error.response) error.response.data = JSON.parse(text) as unknown;
  } catch {
    // Not JSON after all — leave the Blob in place and use the status-based
    // message.
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    await decodeBlobError(error);
    const apiError = normalizeError(error);

    // There is no refresh token in this API: a 401 means the session is over.
    // Public auth endpoints are excluded — a wrong OTP must not log you out.
    if (apiError.kind === 'unauthorized' && !isPublicPath(getRequestUrl(error))) {
      sessionToken.clear();
      onUnauthorized?.();
    }

    return Promise.reject(apiError);
  },
);

function getRequestUrl(error: unknown): string | undefined {
  return axios.isAxiosError(error) ? error.config?.url : undefined;
}

/* ------------------------------- Verbs ----------------------------------- */

/**
 * Thin typed wrappers. Feature code calls these from its `api/` folder only;
 * components reach the network exclusively through TanStack Query hooks.
 *
 * `TResponse` is the decoded body — callers get data, not an AxiosResponse.
 */
export const http = {
  get: async <TResponse>(url: string, config?: AxiosRequestConfig): Promise<TResponse> => {
    const { data } = await apiClient.get<TResponse>(url, config);
    return data;
  },

  post: async <TResponse, TBody = unknown>(
    url: string,
    body?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<TResponse> => {
    const { data } = await apiClient.post<TResponse>(url, body, config);
    return data;
  },

  patch: async <TResponse, TBody = unknown>(
    url: string,
    body?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<TResponse> => {
    const { data } = await apiClient.patch<TResponse>(url, body, config);
    return data;
  },

  delete: async <TResponse>(url: string, config?: AxiosRequestConfig): Promise<TResponse> => {
    const { data } = await apiClient.delete<TResponse>(url, config);
    return data;
  },
} as const;
